import React, { useEffect } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { BillingRow } from "../../../../types";

type InvoiceModalProps = {
  open: boolean;
  data: BillingRow;
  onClose: () => void;
};
const currencySymbols: Record<string, string> = {
  INR: "₹",
  AED: "د.إ",
  USD: "$",
  // ...add more as needed
};
const InvoiceModal: React.FC<InvoiceModalProps> = ({ open, data, onClose }) => {
  const { theme } = useTheme();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const panelBg =
    theme === "dark"
      ? "bg-[#1E1E1E] text-gray-100 border-[#2C2C2C]"
      : "bg-white text-gray-900 border-gray-200";

  // Precise colors to match screenshot
  const dividerColor = theme === "dark" ? "#2F2F2F" : "#E5E7EB"; // inner separators
  const boxBg = theme === "dark" ? "#161616" : "#F9FAFB"; // details box bg
  const boxBorder = theme === "dark" ? "#343434" : "#E5E7EB"; // details box border

  const softCard =
    theme === "dark"
      ? "bg-[#252525] border border-[#2C2C2C] text-gray-200"
      : "bg-gray-50 border border-gray-200 text-gray-800";

  const labelCls = "text-xs text-gray-400";
  const valCls =
    "text-sm font-medium " +
    (theme === "dark" ? "text-gray-200" : "text-gray-800");

  const statusStyles =
    data.status === "Paid"
      ? "bg-[#1f2a1f] text-[#C6D92C] border border-[#2a3a2a]"
      : data.status === "Overdue"
      ? "bg-[#2a1f1f] text-[#ff8a80] border border-[#3a2a2a]"
      : "bg-[#2a2a1f] text-[#E6CF57] border border-[#3a3a2a]";

  // Use API data for invoice details
  const invoiceNo = data.code || "-";
  const paymentMethod = "MASTERCARD 0078"; // Replace with actual if available
  const taxes = data.taxAmount ?? 0;
  const subTotal = (data.totalProductAmount ?? data.total) - taxes;
  const currencySymbol = currencySymbols[data.currency] || data.currency || "₹";

  if (!open) return null;

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/60 z-50"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Right Drawer */}
      <aside
        className={`fixed right-0 top-0 h-full w-full sm:w-[420px] md:w-[460px] z-50 ${panelBg} border-l`}
        role="dialog"
        aria-modal="true"
        style={{ borderLeftColor: dividerColor }}
      >
        {/* Local scrollbar styling for modal content */}
        <style>{`
          .invoice-modal-scrollbar {
            scrollbar-width: thin;
            scrollbar-color: #000 transparent;
          }
          .invoice-modal-scrollbar::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }
          .invoice-modal-scrollbar::-webkit-scrollbar-track {
            background: transparent;
          }
          .invoice-modal-scrollbar::-webkit-scrollbar-thumb {
            background-color: #000;
            border-radius: 8px;
          }
        `}</style>

        <div
          className={`flex items-center justify-between px-4 py-3 border-b`}
          style={{ borderColor: dividerColor }}
        >
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">Invoice</h2>
          </div>
          <span className={`px-2 py-0.5 text-xs rounded-full ${statusStyles}`}>
            {data.status}
          </span>
        </div>

        <div className="h-[calc(100%-56px)] overflow-y-auto px-4 py-4 space-y-4 invoice-modal-scrollbar">
          {/* Details Box (boxy effect with exact borders) */}
          <div
            className="rounded-lg p-4 border"
            style={{ backgroundColor: boxBg, borderColor: boxBorder }}
          >
            <div className="grid grid-cols-1">
              <div
                className="pb-3 border-b border-dashed"
                style={{ borderColor: dividerColor }}
              >
                <div className={labelCls}>End Date</div>
                <div className={valCls}>{data.endDate}</div>
              </div>
              <div
                className="py-3 border-b border-dashed"
                style={{ borderColor: dividerColor }}
              >
                <div className={labelCls}>Invoice Number</div>
                <div className={valCls}>{invoiceNo}</div>
              </div>
              <div className="pt-3">
                <div className={labelCls}>Payment Method</div>
                <div className={valCls}>{paymentMethod}</div>
              </div>
            </div>
          </div>

          {/* Cost Details */}
          <div className="space-y-3">
            <h3 className="text-sm font-semibold">Cost Details</h3>

            <div className={`${softCard} rounded-lg`}>
              <div className="p-4">
                <div className="grid grid-cols-3 text-xs text-gray-400">
                  <div>Type</div>
                  <div className="text-center">Count</div>
                  <div className="text-right">Cost ₹</div>
                </div>
                <div
                  className="mt-2 border-t"
                  style={{ borderColor: dividerColor }}
                />

                <div className="mt-2 space-y-2 text-sm">
                  <div className="grid grid-cols-3 items-center">
                    <div>Premium</div>
                    <div className="text-center">
                      {data.premium ? data.premium.quantity : 0}
                    </div>
                    <div className="text-right">
                      {currencySymbol}
                      {data.premium
                        ? data.premium.totalPrice?.toFixed(2)
                        : "0.00"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 items-center">
                    <div>Basic</div>
                    <div className="text-center">
                      {data.normal ? data.normal.quantity : 0}
                    </div>
                    <div className="text-right">
                      {currencySymbol}
                      {data.normal
                        ? data.normal.totalPrice?.toFixed(2)
                        : "0.00"}
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="px-4 py-3 border-t space-y-2 text-sm"
                style={{ borderColor: dividerColor }}
              >
                <div className="grid grid-cols-2">
                  <div className="text-gray-400">Sub Total</div>
                  <div className="text-right">
                    {currencySymbol}
                    {subTotal?.toFixed(2)}
                  </div>
                </div>
                {data.isCouponApplied && (
                  <div className="grid grid-cols-2">
                    <div
                      className="text-gray-400"
                      style={{ width: "max-content" }}
                    >
                      Discount
                      {data.coupon
                        ? ` (${data.coupon.code} - ${
                            data.coupon.discountType === "PERCENTAGE"
                              ? `${data.coupon.discountPercent}%`
                              : `${currencySymbol}${data.coupon.discountAmount}`
                          })`
                        : ""}
                    </div>
                    <div className="text-right">
                      -{currencySymbol}
                      {data.totalReductionAmount?.toFixed(2)}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2">
                  <div className="text-gray-400">Tax</div>
                  <div className="text-right">
                    {currencySymbol}
                    {taxes?.toFixed(2)}
                  </div>
                </div>
                <div className="grid grid-cols-2 pt-2">
                  <div className="font-semibold">Total</div>
                  <div className="text-right font-semibold">
                    {currencySymbol}
                    {(data.totalAmount ?? data.total)?.toFixed(2)}
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-4 pt-6 mx-auto w-56 sm:w-64">
              <button
                type="button"
                onClick={() => {
                  if (data.invoiceUrl) {
                    window.open(
                      data.invoiceUrl,
                      "_blank",
                      "noopener,noreferrer"
                    );
                  }
                }}
                className="w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg bg-[#B6CB24] text-[#1A1A1A] font-medium hover:bg-[#A8BD1F] transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="25"
                  height="24"
                  viewBox="0 0 25 24"
                  fill="none"
                >
                  <path
                    d="M2.75008 16.75C2.75008 16.1978 3.1978 15.75 3.75008 15.75C4.30237 15.75 4.75008 16.1978 4.75008 16.75C4.75008 17.7444 4.75888 18.0453 4.81844 18.2676C5.00338 18.9578 5.54232 19.4968 6.23251 19.6817C6.45478 19.7412 6.75574 19.75 7.75008 19.75H17.2501L17.8751 19.7481C18.3899 19.7429 18.6009 19.7264 18.7677 19.6817C19.4578 19.4968 19.9968 18.9578 20.1817 18.2676C20.2413 18.0453 20.2501 17.7444 20.2501 16.75C20.2501 16.1978 20.6978 15.75 21.2501 15.75C21.8024 15.75 22.2501 16.1978 22.2501 16.75C22.2501 17.6156 22.2582 18.2445 22.1134 18.7852C21.7435 20.1656 20.6656 21.2435 19.2852 21.6133C18.7445 21.7582 18.1157 21.75 17.2501 21.75H7.75008C6.8845 21.75 6.25565 21.7582 5.71493 21.6133C4.33456 21.2435 3.25667 20.1656 2.8868 18.7852C2.74192 18.2445 2.75008 17.6156 2.75008 16.75Z"
                    fill="#F3F3F6"
                  />
                  <path
                    d="M12.5001 2.25006C11.9478 2.25006 11.5001 2.69777 11.5001 3.25006V13.8595C11.196 13.5544 10.8743 13.2061 10.5606 12.8448C10.0875 12.2999 9.64798 11.7515 9.3253 11.338C9.16437 11.1318 8.896 10.7768 8.80577 10.6573C8.47835 10.2127 7.85201 10.1172 7.40733 10.4444C6.96276 10.7718 6.86726 11.3982 7.19444 11.8429C7.29008 11.9695 7.57986 12.3528 7.74815 12.5684C8.08384 12.9986 8.5465 13.5755 9.04991 14.1554C9.54914 14.7304 10.1087 15.3311 10.6349 15.796C10.8968 16.0274 11.1742 16.2467 11.4503 16.4132C11.699 16.5631 12.0735 16.7501 12.5001 16.7501C12.9267 16.7501 13.3012 16.5631 13.5499 16.4132C13.826 16.2467 14.1034 16.0274 14.3653 15.796C14.8914 15.3311 15.451 14.7303 15.9503 14.1554C16.4537 13.5755 16.9163 12.9986 17.252 12.5684C17.4203 12.3528 17.7101 11.9695 17.8057 11.8429C18.1329 11.3982 18.0374 10.7728 17.5929 10.4454C17.1482 10.1179 16.5219 10.2126 16.1944 10.6573C16.1042 10.7768 15.8358 11.1318 15.6749 11.338C15.3522 11.7514 14.9127 12.2999 14.4395 12.8448C14.1259 13.206 13.8042 13.5544 13.5001 13.8595V3.25006C13.5001 2.69778 13.0524 2.25008 12.5001 2.25006Z"
                    fill="#F3F3F6"
                  />
                </svg>
                Download PDF
              </button>

              <button
                type="button"
                onClick={onClose}
                className={`w-full h-10 rounded-lg font-medium border ${
                  theme === "dark"
                    ? "bg-[#2A2A2A] border-[#3A3A3A] text-gray-200 hover:bg-[#333333]"
                    : "text-[#1A1A1A] bg-[#EDF5C0] border-[#B6CB24] "
                }`}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
};

export default InvoiceModal;
